import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trophy, CheckCircle, XCircle, Brain, Plus, Clock, Eye, Circle, Pause, Play, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const DRAFT_KEY = (quizId) => `sbl_quiz_draft_${quizId}`;

// Quiz Taking Component
function TakeQuiz({ quiz, onComplete }) {
  // Restore draft if any
  const draft = (() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY(quiz.id)) || "null"); } catch { return null; }
  })();
  const [answers, setAnswers] = useState(draft?.answers || {});
  const [submitting, setSubmitting] = useState(false);
  const [leaveWarning, setLeaveWarning] = useState(false);
  const [paused, setPaused] = useState(false);
  // Time-limit handling: total seconds remaining. Persisted across pauses via localStorage.
  const initialSeconds = quiz.time_limit > 0 ? quiz.time_limit * 60 : 0;
  // Bug fix (Round 39 · Task 2) — only trust the cached draft.secondsLeft when it
  // is a positive number AND ≤ the current quiz's time_limit. If an admin
  // extended the quiz's time_limit or the draft is stale (e.g. secondsLeft > new
  // initialSeconds), fall back to initialSeconds so the fresh timer wins.
  const cachedSeconds = (draft && typeof draft.secondsLeft === "number" && draft.secondsLeft > 0 && draft.secondsLeft <= initialSeconds)
    ? draft.secondsLeft
    : initialSeconds;
  const [secondsLeft, setSecondsLeft] = useState(cachedSeconds);
  const submittedRef = useRef(false);

  // Persist draft on every change
  useEffect(() => {
    if (submittedRef.current) return;
    localStorage.setItem(DRAFT_KEY(quiz.id), JSON.stringify({ answers, secondsLeft, paused }));
  }, [answers, secondsLeft, paused, quiz.id]);

  // Countdown ticker
  useEffect(() => {
    if (paused || initialSeconds === 0 || submittedRef.current) return;
    if (secondsLeft <= 0) {
      // auto-submit
      if (!submittedRef.current) {
        submittedRef.current = true;
        toast.warning("Time's up — auto-submitting your quiz");
        doSubmit(true);
      }
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, paused, initialSeconds]);

  // Quiz lockdown: prevent page leave and copy (only while not paused — paused means student is taking a break)
  useEffect(() => {
    if (paused) return;
    const handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = "You have an active quiz. Are you sure you want to leave?"; };
    const handleVisibility = () => { if (document.hidden) setLeaveWarning(true); };
    const handleCopy = (e) => { e.preventDefault(); };
    const handleContextMenu = (e) => { e.preventDefault(); };
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'a' || e.key === 'p')) { e.preventDefault(); }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibility);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibility);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [paused]);

  const fmtTime = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const doSubmit = async (auto = false) => {
    const answersList = quiz.questions.map(q => ({
      question_id: q.id,
      selected_answer: answers[q.id] !== undefined ? parseInt(answers[q.id]) : -1,
    }));
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { answers: answersList });
      submittedRef.current = true;
      localStorage.removeItem(DRAFT_KEY(quiz.id));
      if (auto) toast.info("Quiz submitted automatically due to time-up");
      onComplete(data);
    } catch (e) { toast.error("Failed to submit quiz"); }
    setSubmitting(false);
  };

  const handleSubmit = () => doSubmit(false);

  const togglePause = () => {
    setPaused((p) => {
      const next = !p;
      if (next) toast.success("Quiz paused — your draft is saved. Questions are hidden until you resume.");
      else toast.success("Quiz resumed");
      return next;
    });
  };

  return (
    <div className="space-y-6" style={{ userSelect: "none", WebkitUserSelect: "none" }} data-testid="quiz-lockdown">
      {/* Sticky timer + pause bar */}
      <div className="sticky top-0 z-20 bg-white border border-slate-200 rounded-none shadow-sm p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="quiz-timer-bar">
        <div className="flex items-center gap-3">
          {initialSeconds > 0 ? (
            <div className={`flex items-center gap-1.5 font-mono text-lg font-bold ${secondsLeft <= 30 ? "text-red-600 animate-pulse" : "text-slate-950"}`} data-testid="quiz-timer-display">
              <Clock size={18} /> {fmtTime(secondsLeft)}
            </div>
          ) : (
            <div className="text-xs text-slate-500">No time limit</div>
          )}
          <Badge className="rounded-none text-[10px] bg-slate-100 text-slate-700">
            {Object.keys(answers).length} / {quiz.questions.length} answered
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={togglePause}
            variant="outline"
            size="sm"
            className="rounded-none gap-1 text-xs"
            data-testid="quiz-pause-btn"
          >
            {paused ? <><Play size={13} weight="fill" /> Resume</> : <><Pause size={13} weight="fill" /> Pause / Save Draft</>}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || paused}
            size="sm"
            className="bg-blue-800 text-white hover:bg-blue-900 rounded-none px-4 text-xs"
            data-testid="submit-quiz-btn"
          >
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </div>

      {leaveWarning && !paused && (
        <div className="bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-sm text-red-800" data-testid="quiz-leave-warning">
          <XCircle size={18} weight="fill" className="text-red-500 flex-shrink-0" />
          <span><strong>Warning:</strong> You left the quiz page. This activity has been recorded. Please stay on this page until you submit.</span>
          <button onClick={() => setLeaveWarning(false)} className="ml-auto text-xs text-red-600 underline">Dismiss</button>
        </div>
      )}

      {paused ? (
        <Card className="border-2 border-amber-300 bg-amber-50/60 rounded-none p-10 text-center" data-testid="quiz-paused-screen">
          <Pause size={48} weight="fill" className="text-amber-500 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Quiz paused
          </h3>
          <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
            Your answers are saved. Questions are hidden so you can take a break — click <strong>Resume</strong> when you're ready.
          </p>
          {initialSeconds > 0 && (
            <p className="text-xs text-amber-700 mt-3">
              Timer paused at <span className="font-mono font-bold">{fmtTime(secondsLeft)}</span>
            </p>
          )}
          <Button
            onClick={togglePause}
            className="bg-blue-800 text-white hover:bg-blue-900 rounded-none mt-5 gap-2"
            data-testid="quiz-resume-btn"
          >
            <Play size={14} weight="fill" /> Resume Quiz
          </Button>
        </Card>
      ) : (
        <>
          {quiz.questions.map((q, idx) => (
            <Card key={q.id} className="border border-slate-200 rounded-none shadow-sm" data-testid={`quiz-question-${idx}`}>
              <CardContent className="p-5">
                <p className="font-semibold text-slate-900 mb-3">
                  <span className="text-blue-800 mr-2">Q{idx + 1}.</span>{q.question}
                </p>
                <RadioGroup value={answers[q.id]?.toString()} onValueChange={v => setAnswers({ ...answers, [q.id]: v })}>
                  {q.options?.map((opt, oIdx) => (
                    <div key={oIdx} className="flex items-center space-x-3 py-1.5">
                      <RadioGroupItem value={oIdx.toString()} id={`q${idx}-o${oIdx}`} data-testid={`quiz-option-${idx}-${oIdx}`} />
                      <Label htmlFor={`q${idx}-o${oIdx}`} className="text-sm text-slate-700 cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// Quiz Results Component
function QuizResults({ result, quiz }) {
  return (
    <div className="space-y-6">
      <Card className={`border rounded-none shadow-sm ${result.passed ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
        <CardContent className="p-6 text-center">
          {result.passed ? (
            <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto mb-3" />
          ) : (
            <XCircle size={48} weight="fill" className="text-red-500 mx-auto mb-3" />
          )}
          <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {result.passed ? "Congratulations!" : "Keep Learning"}
          </h2>
          <p className="text-slate-600 mb-4">
            {result.passed ? "You passed the quiz!" : `You need ${quiz?.pass_percentage || 50}% to pass. Review the module and try again.`}
          </p>
          {result.is_retake && (
            <Badge className="rounded-none text-xs bg-amber-100 text-amber-800 mb-3">Retake (Attempt #{result.attempt_number})</Badge>
          )}
          <div className="flex items-center justify-center gap-6">
            <div>
              <p className="text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{result.percentage}%</p>
              <p className="text-xs text-slate-500">Score</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{result.score}/{result.total_points}</p>
              <p className="text-xs text-slate-500">Points</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Review */}
      {result.results && quiz?.questions && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Review</h3>
          {quiz.questions.map((q, idx) => {
            const r = result.results[idx];
            return (
              <Card key={q.id} className={`border rounded-none shadow-sm ${r?.correct ? "border-emerald-200" : "border-red-200"}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    {r?.correct ? <CheckCircle size={18} className="text-emerald-600 mt-0.5" weight="fill" /> : <XCircle size={18} className="text-red-500 mt-0.5" weight="fill" />}
                    <div>
                      <p className="text-sm font-medium text-slate-900">{q.question}</p>
                      <p className="text-xs text-slate-500 mt-1">Correct: {q.options?.[q.correct_answer]}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function QuizPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [result, setResult] = useState(null);
  const [prevSubmission, setPrevSubmission] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [prereqGap, setPrereqGap] = useState(null); // { missing: 'modules' } | null
  const isStudent = user?.role === "student";
  const canEdit = ["super_admin", "admin", "instructor"].includes(user?.role);

  useEffect(() => {
    api.get(`/quizzes/${id}`).then(async (r) => {
      const q = r.data;
      setQuiz(q);
      // Round 37/38 — gate quiz form for students. Two branches:
      //   • Module quiz (q.module_id set): only gated on that module's lessons.
      //   • End-of-course quiz (no module_id): use course-level completion-status.
      if (q?.course_id && !canEdit) {
        try {
          const moduleId = q.module_id || "";
          if (moduleId) {
            const { data } = await api.get(`/courses/${q.course_id}/module-progression`);
            const modules = Array.isArray(data) ? data : (data?.modules || []);
            const mine = modules.find(m => (m.id || m.module_id) === moduleId);
            if (mine && mine.lessons_complete === false) {
              setPrereqGap({
                scope: "module",
                message: "Complete this module's lessons before starting the quiz.",
                module_title: mine.title || mine.module_title || "",
              });
            }
          } else {
            const { data } = await api.get(`/courses/${q.course_id}/completion-status`);
            const steps = data?.steps || [];
            const nextStep = data?.next_step;
            const done = data?.done || [];
            const qIdx = steps.indexOf("quizzes");
            if (qIdx >= 0 && nextStep && steps.indexOf(nextStep) < qIdx && !done.includes("quizzes")) {
              setPrereqGap({ scope: "course", missing: nextStep, steps, done });
            }
          }
        } catch { /* ignore — server guard is the source of truth */ }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    // Check for previous submission
    api.get(`/quizzes/${id}/my-submission`).then(r => {
      if (r.data && r.data.id) setPrevSubmission(r.data);
    }).catch(() => {});
  }, [id, canEdit]);

  if (loading) return <Layout><div className="p-8"><div className="h-64 bg-slate-100 animate-pulse" /></div></Layout>;
  if (!quiz) return <Layout><div className="p-8 text-center"><p className="text-slate-500">Quiz not found</p></div></Layout>;

  const hasPassed = prevSubmission?.passed === true;
  const hasFailed = prevSubmission && !prevSubmission.passed;

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="quiz-page">
        <Link to={quiz.course_id ? `/courses/${quiz.course_id}` : "/courses"} className="text-sm text-blue-800 hover:underline mb-4 inline-block">
          &larr; Back to Course
        </Link>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="rounded-none text-xs">Quiz</Badge>
            {hasPassed && <Badge className="rounded-none text-xs bg-emerald-600 text-white">Completed</Badge>}
            {hasFailed && <Badge className="rounded-none text-xs bg-amber-500 text-white">Retake Required</Badge>}
            {canEdit && (
              <Link to={`/quizzes/${id}/edit`}><Badge variant="outline" className="rounded-none text-xs cursor-pointer hover:bg-blue-50">Edit Quiz</Badge></Link>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {quiz.title}
          </h1>
          {quiz.description && <p className="text-slate-600 mt-2">{quiz.description}</p>}
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
            <span>{quiz.questions?.length || 0} questions</span>
            {quiz.time_limit > 0 && <span className="flex items-center gap-1"><Clock size={14} /> {quiz.time_limit} min</span>}
            <span>Pass: {quiz.pass_percentage}%</span>
          </div>
        </div>

        {result ? (
          <QuizResults result={result} quiz={quiz} />
        ) : prereqGap && !canEdit ? (
          /* Round 37/38 — quiz prerequisites missing. Message varies for
             module quiz vs course-level end-of-course assessment. */
          <Card className="border-2 border-amber-300 bg-amber-50/60 rounded-none p-8 text-center" data-testid="quiz-locked">
            <Lock size={40} weight="fill" className="text-amber-500 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-slate-950 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Quiz locked
            </h3>
            {prereqGap.scope === "module" ? (
              <>
                <p className="text-sm text-slate-700 max-w-md mx-auto" data-testid="quiz-locked-message">
                  Complete this module{prereqGap.module_title ? ` (${prereqGap.module_title})` : ""}&apos;s lessons before starting the quiz.
                </p>
                <p className="text-xs text-amber-800 mt-3">
                  Every lesson under this module must be marked complete first.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-700 max-w-md mx-auto">
                  Finish the <span className="font-bold capitalize" data-testid="quiz-locked-missing">{(prereqGap.missing || "").replace("_", " ")}</span>{" "}
                  step before starting this quiz.
                </p>
                <p className="text-xs text-amber-800 mt-3">
                  Completion order for this course:{" "}
                  <span className="font-mono">{(prereqGap.steps || []).join(" → ")}</span>
                </p>
              </>
            )}
            {quiz.course_id && (
              <Link to={`/courses/${quiz.course_id}`} className="inline-block mt-5">
                <Button variant="outline" className="rounded-none text-sm" data-testid="quiz-locked-back">
                  &larr; Back to course
                </Button>
              </Link>
            )}
          </Card>
        ) : hasPassed && !reviewMode ? (
          /* Already passed — show result summary and review option */
          <div>
            <QuizResults result={prevSubmission} quiz={quiz} />
            <div className="mt-4 text-center">
              <Button onClick={() => setReviewMode(true)} variant="outline" className="rounded-none gap-2 text-sm" data-testid="review-quiz-btn">
                <Eye size={14} /> Review Your Answers
              </Button>
            </div>
          </div>
        ) : hasPassed && reviewMode ? (
          /* Review mode — read-only answers */
          <div className="space-y-4" data-testid="quiz-review">
            <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle size={16} weight="fill" /> You've already passed this quiz. This is a read-only review.
            </div>
            {quiz.questions.map((q, idx) => {
              const r = prevSubmission?.results?.[idx];
              return (
                <Card key={idx} className="border border-slate-200 rounded-none shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-sm font-semibold text-slate-900 mb-3">Q{idx+1}. {q.question}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <div key={oIdx} className={`p-2 border text-sm flex items-center gap-2 ${oIdx === q.correct_answer ? "border-emerald-400 bg-emerald-50" : r?.selected === oIdx && !r?.correct ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
                          {oIdx === q.correct_answer ? <CheckCircle size={14} weight="fill" className="text-emerald-600" /> : r?.selected === oIdx ? <XCircle size={14} weight="fill" className="text-red-500" /> : <Circle size={14} className="text-slate-300" />}
                          <span>{opt}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            <Button onClick={() => setReviewMode(false)} variant="outline" className="rounded-none text-sm">&larr; Back to Summary</Button>
          </div>
        ) : hasFailed ? (
          /* Failed — option to retake */
          <div>
            <QuizResults result={prevSubmission} quiz={quiz} />
            <div className="mt-4 bg-amber-50 border border-amber-200 p-4 text-center">
              <p className="text-sm text-amber-800 mb-3">You need to review the module content and retake this quiz.</p>
              <Button
                onClick={() => {
                  // Bug fix (Round 39 · Task 1) — clear the persisted draft so the
                  // retake doesn't reload the previous attempt's answers or timer.
                  try { localStorage.removeItem(DRAFT_KEY(quiz.id)); } catch (_) {}
                  setPrevSubmission(null);
                }}
                className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2"
                data-testid="retake-quiz-btn"
              >
                Retake Quiz
              </Button>
            </div>
          </div>
        ) : (
          <TakeQuiz quiz={quiz} onComplete={setResult} />
        )}
      </div>
    </Layout>
  );
}

// Create Quiz Page
export function CreateQuizPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", time_limit: 30, pass_percentage: 50, questions: [], module_id: "" });
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiContent, setAiContent] = useState("");
  const [aiDialog, setAiDialog] = useState(false);
  const [numQuestions, setNumQuestions] = useState(5);
  // Round 38 — module dropdown for gating quizzes to a specific module.
  const [modulesList, setModulesList] = useState([]);
  useEffect(() => {
    api.get(`/courses/${courseId}/modules`)
      .then(r => setModulesList(r.data || []))
      .catch(() => setModulesList([]));
  }, [courseId]);

  const addQuestion = () => {
    setForm({
      ...form,
      questions: [...form.questions, { id: Date.now().toString(), question: "", options: ["", "", "", ""], correct_answer: 0, points: 1 }],
    });
  };

  const updateQuestion = (idx, field, value) => {
    const qs = [...form.questions];
    qs[idx] = { ...qs[idx], [field]: value };
    setForm({ ...form, questions: qs });
  };

  const updateOption = (qIdx, oIdx, value) => {
    const qs = [...form.questions];
    const opts = [...qs[qIdx].options];
    opts[oIdx] = value;
    qs[qIdx] = { ...qs[qIdx], options: opts };
    setForm({ ...form, questions: qs });
  };

  const removeQuestion = (idx) => {
    setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  };

  const aiGenerate = async () => {
    if (!aiContent || aiContent.trim().length < 20) {
      toast.error("Please provide at least 20 characters of content for question generation");
      return;
    }
    setAiGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-quiz", { content: aiContent, num_questions: numQuestions });
      if (data.questions?.length > 0) {
        setForm({ ...form, questions: [...form.questions, ...data.questions] });
        toast.success(`Generated ${data.questions.length} questions`);
        setAiDialog(false);
        setAiContent("");
      } else {
        toast.error("No questions generated. Try providing more detailed content.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "AI generation failed. Please try again.";
      toast.error(detail);
    }
    setAiGenerating(false);
  };

  const handleSave = async () => {
    if (!form.title || form.questions.length === 0) {
      toast.error("Please add a title and at least one question");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/courses/${courseId}/quizzes`, {
        ...form,
        time_limit: parseInt(form.time_limit) || 30,
        pass_percentage: parseFloat(form.pass_percentage) || 50,
        quiz_type: "mcq",
        module_id: form.module_id || "",
      });
      toast.success("Quiz created!");
      navigate(`/courses/${courseId}`);
    } catch (e) { toast.error("Failed to create quiz"); }
    setSaving(false);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="create-quiz-page">
        <Link to={`/courses/${courseId}`} className="text-sm text-blue-800 hover:underline mb-4 inline-block">&larr; Back to Course</Link>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Create Quiz</h1>
          <Button onClick={() => setAiDialog(true)} variant="outline" className="rounded-none gap-2 border-blue-800 text-blue-800" data-testid="ai-generate-btn">
            <Brain size={16} /> AI Generate
          </Button>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <Label className="text-sm font-medium">Quiz Title</Label>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none" data-testid="quiz-title-input" placeholder="e.g. Module 1 Assessment" />
          </div>
          <div>
            <Label className="text-sm font-medium">Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 rounded-none" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Time Limit (min)</Label>
              <Input type="number" value={form.time_limit} onChange={e => setForm({...form, time_limit: e.target.value})} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label className="text-sm font-medium">Pass Percentage</Label>
              <Input type="number" value={form.pass_percentage} onChange={e => setForm({...form, pass_percentage: e.target.value})} className="mt-1 rounded-none" />
            </div>
          </div>
          {/* Round 38 — module attachment */}
          <div>
            <Label className="text-sm font-medium">Module (leave blank for End-of-Course Assessment)</Label>
            <Select value={form.module_id || "__none__"} onValueChange={v => setForm({ ...form, module_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1 rounded-none" data-testid="quiz-module-select"><SelectValue placeholder="End of Course Assessment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">End of Course Assessment (no module)</SelectItem>
                {modulesList.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Attach to a module to gate this quiz on that module&apos;s lessons. Leave blank to make it the end-of-course assessment.
            </p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Questions ({form.questions.length})
        </h3>

        <div className="space-y-4 mb-6">
          {form.questions.map((q, idx) => (
            <Card key={q.id || idx} className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-bold text-blue-800">Question {idx + 1}</span>
                  <Button size="sm" variant="ghost" className="text-red-500 text-xs h-6 px-2" onClick={() => removeQuestion(idx)}>Remove</Button>
                </div>
                <Input value={q.question} onChange={e => updateQuestion(idx, "question", e.target.value)} placeholder="Question text" className="mb-3 rounded-none" />
                <div className="space-y-2">
                  {q.options.map((opt, oIdx) => (
                    <div key={oIdx} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`correct-${idx}`}
                        checked={q.correct_answer === oIdx}
                        onChange={() => updateQuestion(idx, "correct_answer", oIdx)}
                        className="accent-blue-800"
                      />
                      <Input value={opt} onChange={e => updateOption(idx, oIdx, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oIdx)}`} className="rounded-none flex-1" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-3">
          <Button onClick={addQuestion} variant="outline" className="rounded-none gap-1" data-testid="add-question-btn">
            <Plus size={14} /> Add Question
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-quiz-btn">
            {saving ? "Saving..." : "Save Quiz"}
          </Button>
        </div>

        <Dialog open={aiDialog} onOpenChange={setAiDialog}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>AI Quiz Generator</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-slate-600">Paste lesson content and AI will generate MCQ questions for you.</p>
              <div>
                <Label className="text-sm">Content</Label>
                <Textarea value={aiContent} onChange={e => setAiContent(e.target.value)} className="mt-1 rounded-none" rows={6} placeholder="Paste your lesson content here..." data-testid="ai-content-input" />
              </div>
              <div>
                <Label className="text-sm">Number of Questions</Label>
                <Input type="number" value={numQuestions} onChange={e => setNumQuestions(parseInt(e.target.value) || 5)} className="mt-1 rounded-none" min={1} max={20} />
              </div>
              <Button onClick={aiGenerate} disabled={aiGenerating || !aiContent} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="generate-questions-btn">
                <Brain size={16} /> {aiGenerating ? "Generating..." : "Generate Questions"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}


// Edit Quiz Page
export function EditQuizPage() {
  const { quizId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiContent, setAiContent] = useState("");
  const [aiDialog, setAiDialog] = useState(false);
  const [numQuestions, setNumQuestions] = useState(5);

  useEffect(() => {
    api.get(`/quizzes/${quizId}`).then(r => {
      const q = r.data;
      setForm({
        title: q.title || "",
        description: q.description || "",
        time_limit: q.time_limit || 30,
        pass_percentage: q.pass_percentage || 50,
        questions: q.questions || [],
        course_id: q.course_id || "",
        module_id: q.module_id || "",
      });
      setLoading(false);
      // Round 38 — pull the course's modules so admins can attach the quiz.
      if (q.course_id) {
        api.get(`/courses/${q.course_id}/modules`)
          .then(r2 => setModulesList(r2.data || []))
          .catch(() => setModulesList([]));
      }
    }).catch(() => setLoading(false));
  }, [quizId]);
  const [modulesList, setModulesList] = useState([]);

  if (loading) return <Layout><div className="p-8"><div className="h-64 bg-slate-100 animate-pulse" /></div></Layout>;
  if (!form) return <Layout><div className="p-8 text-center"><p className="text-slate-500">Quiz not found</p></div></Layout>;

  const addQuestion = () => {
    setForm({ ...form, questions: [...form.questions, { id: Date.now().toString(), question: "", options: ["", "", "", ""], correct_answer: 0, points: 1 }] });
  };

  const updateQuestion = (idx, field, value) => {
    const qs = [...form.questions]; qs[idx] = { ...qs[idx], [field]: value }; setForm({ ...form, questions: qs });
  };

  const updateOption = (qIdx, oIdx, value) => {
    const qs = [...form.questions]; const opts = [...qs[qIdx].options]; opts[oIdx] = value; qs[qIdx] = { ...qs[qIdx], options: opts }; setForm({ ...form, questions: qs });
  };

  const removeQuestion = (idx) => {
    setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  };

  const aiGenerate = async () => {
    if (!aiContent || aiContent.trim().length < 20) { toast.error("Provide at least 20 characters of content"); return; }
    setAiGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-quiz", { content: aiContent, num_questions: numQuestions });
      if (data.questions?.length > 0) {
        setForm({ ...form, questions: [...form.questions, ...data.questions] });
        toast.success(`Added ${data.questions.length} questions`);
        setAiDialog(false); setAiContent("");
      } else { toast.error("No questions generated"); }
    } catch (e) { toast.error(e.response?.data?.detail || "AI generation failed"); }
    setAiGenerating(false);
  };

  const handleSave = async () => {
    if (!form.title || form.questions.length === 0) { toast.error("Add a title and at least one question"); return; }
    setSaving(true);
    try {
      await api.put(`/quizzes/${quizId}`, {
        title: form.title, description: form.description,
        time_limit: parseInt(form.time_limit) || 30,
        pass_percentage: parseFloat(form.pass_percentage) || 50,
        questions: form.questions, quiz_type: "mcq",
        module_id: form.module_id || "",
      });
      // Round 38 — also hit the dedicated attach endpoint so is_end_of_course flips.
      try {
        await api.put(`/quizzes/${quizId}/module`, { module_id: form.module_id || "" });
      } catch (_) { /* non-blocking */ }
      toast.success("Quiz updated!");
      navigate(form.course_id ? `/courses/${form.course_id}` : "/courses");
    } catch (e) { toast.error("Failed to update quiz"); }
    setSaving(false);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="edit-quiz-page">
        <Link to={form.course_id ? `/courses/${form.course_id}` : "/courses"} className="text-sm text-blue-800 hover:underline mb-4 inline-block">&larr; Back to Course</Link>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Quiz</h1>
          <Button onClick={() => setAiDialog(true)} variant="outline" className="rounded-none gap-2 border-blue-800 text-blue-800" data-testid="ai-generate-btn">
            <Brain size={16} /> AI Generate More
          </Button>
        </div>

        <div className="space-y-4 mb-6">
          <div><Label className="text-sm font-medium">Quiz Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none" data-testid="edit-quiz-title" /></div>
          <div><Label className="text-sm font-medium">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 rounded-none" rows={2} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-medium">Time Limit (min)</Label><Input type="number" value={form.time_limit} onChange={e => setForm({...form, time_limit: e.target.value})} className="mt-1 rounded-none" /></div>
            <div><Label className="text-sm font-medium">Pass Percentage</Label><Input type="number" value={form.pass_percentage} onChange={e => setForm({...form, pass_percentage: e.target.value})} className="mt-1 rounded-none" /></div>
          </div>
          {/* Round 38 — module attachment */}
          <div>
            <Label className="text-sm font-medium">Module (leave blank for End-of-Course Assessment)</Label>
            <Select value={form.module_id || "__none__"} onValueChange={v => setForm({ ...form, module_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1 rounded-none" data-testid="edit-quiz-module-select"><SelectValue placeholder="End of Course Assessment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">End of Course Assessment (no module)</SelectItem>
                {modulesList.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Attach to a module to gate this quiz on that module&apos;s lessons. Leave blank to make it the end-of-course assessment.
            </p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Questions ({form.questions.length})</h3>

        <div className="space-y-4 mb-6">
          {form.questions.map((q, idx) => (
            <Card key={q.id || idx} className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-bold text-blue-800">Question {idx + 1}</span>
                  <Button size="sm" variant="ghost" className="text-red-500 text-xs h-6 px-2" onClick={() => removeQuestion(idx)}>Remove</Button>
                </div>
                <Input value={q.question} onChange={e => updateQuestion(idx, "question", e.target.value)} placeholder="Question text" className="mb-3 rounded-none" />
                <div className="space-y-2">
                  {(q.options || []).map((opt, oIdx) => (
                    <div key={oIdx} className="flex items-center gap-2">
                      <input type="radio" name={`correct-edit-${idx}`} checked={q.correct_answer === oIdx} onChange={() => updateQuestion(idx, "correct_answer", oIdx)} className="accent-blue-800" />
                      <Input value={opt} onChange={e => updateOption(idx, oIdx, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oIdx)}`} className="rounded-none flex-1" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-3">
          <Button onClick={addQuestion} variant="outline" className="rounded-none gap-1" data-testid="add-question-btn"><Plus size={14} /> Add Question</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-quiz-btn">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <Dialog open={aiDialog} onOpenChange={setAiDialog}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>AI Quiz Generator</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-slate-600">Paste content and AI will generate additional MCQ questions.</p>
              <Textarea value={aiContent} onChange={e => setAiContent(e.target.value)} className="rounded-none" rows={6} placeholder="Paste lesson content here..." data-testid="ai-content-input" />
              <div><Label className="text-sm">Number of Questions</Label><Input type="number" value={numQuestions} onChange={e => setNumQuestions(parseInt(e.target.value) || 5)} className="mt-1 rounded-none" min={1} max={20} /></div>
              <Button onClick={aiGenerate} disabled={aiGenerating || !aiContent} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="generate-questions-btn">
                <Brain size={16} /> {aiGenerating ? "Generating..." : "Generate Questions"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
